import { useState, useEffect, useRef } from "react";
import "@/App.css";
import { Phone, MessageCircle, MapPin, Clock, Star, ChevronDown, ChevronLeft, ChevronRight, Instagram, Menu, X, Calendar, User, Scissors, Check, Mail } from "lucide-react";
import LightRays from "./components/LightRays";
import axios from "axios";

const API = '/api';

// Configuration du salon
const SALON_CONFIG = {
  name: "LE GRAND",
  nameLine2: "PARIS",
  location: "CHAMPIGNY-SUR-MARNE",
  tagline: "Coiffure Homme & Femme — Barber Shop",
  phone: "06 69 55 75 17",
  whatsapp: "https://wa.me/33669557517",
  address: "149 Av. Roger Salengro, 94500 Champigny-sur-Marne",
  googleMapsUrl: "https://maps.google.com/?q=149+Av+Roger+Salengro+94500+Champigny-sur-Marne",
  rating: 5.0,
  reviewCount: 48,
  instagram: "https://instagram.com/legrandparis",
  tiktok: "https://tiktok.com/@legrandparis",
  hours: [
    { day: "Lundi", time: "09h30 - 19h30" },
    { day: "Mardi", time: "09h30 - 19h30" },
    { day: "Mercredi", time: "09h30 - 19h30" },
    { day: "Jeudi", time: "09h30 - 19h30" },
    { day: "Vendredi", time: "08h30 - 19h30" },
    { day: "Samedi", time: "09h30 - 19h30" },
    { day: "Dimanche", time: "09h30 - 19h30" },
  ]
};

// Services du salon
const SERVICES = [
  {
    title: "Coupe Homme",
    description: "Coupes classiques et modernes, dégradés américains ou européens. Nos barbers experts subliment votre style.",
    image: "https://images.unsplash.com/photo-1599351431202-1e0f0137899a?w=600&h=400&fit=crop"
  },
  {
    title: "Taille de Barbe",
    description: "Sculptez votre barbe avec précision. Taille, rasage traditionnel et soins pour une barbe parfaitement entretenue.",
    image: "https://images.unsplash.com/photo-1621607512022-6aecc4fed814?w=600&h=400&fit=crop"
  },
  {
    title: "Coiffure Femme",
    description: "Coupes, brushings, lissages et colorations. Soraya et notre équipe prennent soin de vos cheveux.",
    image: "https://images.unsplash.com/photo-1560066984-138dadb4c035?w=600&h=400&fit=crop"
  },
  {
    title: "Soins & Traitements",
    description: "Lissage brésilien, soins capillaires, colorations professionnelles pour des résultats exceptionnels.",
    image: "https://images.unsplash.com/photo-1522337360788-8b13dee7a37e?w=600&h=400&fit=crop"
  }
];

// Photos de la galerie
const GALLERY_PHOTOS = [
  "https://images.unsplash.com/photo-1622286342621-4bd786c2447c?w=600&h=800&fit=crop",
  "https://images.unsplash.com/photo-1560066984-138dadb4c035?w=400&h=400&fit=crop",
  "https://images.unsplash.com/photo-1605497788044-5a32c7078486?w=400&h=400&fit=crop",
  "https://images.unsplash.com/photo-1599351431202-1e0f0137899a?w=400&h=300&fit=crop",
  "https://images.unsplash.com/photo-1621607512022-6aecc4fed814?w=400&h=300&fit=crop",
  "https://images.unsplash.com/photo-1522337360788-8b13dee7a37e?w=400&h=300&fit=crop"
];

// Avis clients
const REVIEWS = [
  {
    id: 1,
    name: "S d",
    rating: 5,
    text: "Soraya est enfin de retour ! Comme toujours, ses prestations sont impeccables et son accueil est tout aussi chaleureux et professionnel. (Côté femme)",
    date: "Il y a 3 semaines"
  },
  {
    id: 2,
    name: "Attou",
    rating: 5,
    text: "Accueil chaleureux ! Un vrai professionnel qui a pris le temps de m'écouter et le résultat est magnifique !",
    date: "Il y a 3 semaines"
  },
  {
    id: 3,
    name: "Esth Fan",
    rating: 5,
    text: "Des coiffeurs adorables ! Soraya est très pro ! Lissage magnifique ! Merci beaucoup",
    date: "Il y a 1 mois"
  },
  {
    id: 4,
    name: "Reda B.",
    rating: 5,
    text: "Kamel m'a coiffé et franchement rien à dire, barbelette dégradé super je suis très content",
    date: "Il y a 3 semaines"
  },
  {
    id: 5,
    name: "Younes",
    rating: 5,
    text: "Super expérience ! Super coupe et super taille de barbe",
    date: "Il y a 1 jour"
  }
];

// Composant Modal de Réservation
const BookingModal = ({ isOpen, onClose }) => {
  const [step, setStep] = useState(1);
  const [services, setServices] = useState([]);
  const [barbers, setBarbers] = useState([]);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedBarber, setSelectedBarber] = useState(null);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [bookingResult, setBookingResult] = useState(null);
  const [activeCategory, setActiveCategory] = useState('homme');
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchServices();
      fetchBarbers();
    }
  }, [isOpen]);

  useEffect(() => {
    if (selectedDate && selectedBarber) {
      fetchAvailability();
    }
  }, [selectedDate, selectedBarber]);

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API}/services`);
      setServices(response.data);
    } catch (error) {
      console.error('Error fetching services:', error);
    }
  };

  const fetchBarbers = async () => {
    try {
      const response = await axios.get(`${API}/barbers`);
      setBarbers(response.data);
    } catch (error) {
      console.error('Error fetching barbers:', error);
    }
  };

  const fetchAvailability = async () => {
    if (!selectedDate || !selectedBarber) return;
    setLoadingSlots(true);
    try {
      const response = await axios.get(`${API}/availability/${selectedDate}?barber_id=${selectedBarber.id}`);
      setAvailableSlots(response.data.slots);
    } catch (error) {
      console.error('Error fetching availability:', error);
    } finally {
      setLoadingSlots(false);
    }
  };

  const handleSubmit = async () => {
    if (!selectedService || !selectedBarber || !selectedDate || !selectedTime || !customerName || !customerEmail || !customerPhone) {
      alert('Veuillez remplir tous les champs');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/bookings`, {
        customer_name: customerName,
        customer_email: customerEmail,
        customer_phone: customerPhone,
        service_id: selectedService.id,
        service_name: selectedService.name,
        service_price: selectedService.price,
        barber_id: selectedBarber.id,
        barber_name: selectedBarber.name,
        date: selectedDate,
        time: selectedTime
      });
      
      setBookingResult(response.data);
      setStep(5);
    } catch (error) {
      console.error('Error creating booking:', error);
      const errorMsg = error.response?.data?.detail || 'Erreur lors de la réservation. Veuillez réessayer.';
      alert(errorMsg);
      // Refresh availability if slot was taken
      fetchAvailability();
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setStep(1);
    setSelectedService(null);
    setSelectedBarber(null);
    setSelectedDate('');
    setSelectedTime('');
    setCustomerName('');
    setCustomerEmail('');
    setCustomerPhone('');
    setBookingResult(null);
    onClose();
  };

  const getMinDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  const getMaxDate = () => {
    const maxDate = new Date();
    maxDate.setMonth(maxDate.getMonth() + 2);
    return maxDate.toISOString().split('T')[0];
  };

  const filteredServices = services.filter(s => s.category === activeCategory);

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose} data-testid="booking-modal">
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose} data-testid="modal-close">
          <X size={24} />
        </button>
        
        <div className="modal-header">
          <h2>Réserver un RDV</h2>
          <div className="steps-indicator">
            {[1, 2, 3, 4].map(s => (
              <div key={s} className={`step-dot ${step >= s ? 'active' : ''}`} />
            ))}
          </div>
        </div>

        {/* Step 1: Choose Service */}
        {step === 1 && (
          <div className="modal-step" data-testid="step-service">
            <h3><Scissors size={20} /> Choisissez votre prestation</h3>
            
            <div className="category-tabs">
              <button 
                className={`tab ${activeCategory === 'homme' ? 'active' : ''}`}
                onClick={() => setActiveCategory('homme')}
              >
                Homme
              </button>
              <button 
                className={`tab ${activeCategory === 'barbe' ? 'active' : ''}`}
                onClick={() => setActiveCategory('barbe')}
              >
                Barbe
              </button>
              <button 
                className={`tab ${activeCategory === 'femme' ? 'active' : ''}`}
                onClick={() => setActiveCategory('femme')}
              >
                Femme
              </button>
            </div>

            <div className="services-list">
              {filteredServices.map(service => (
                <div 
                  key={service.id}
                  className={`service-option ${selectedService?.id === service.id ? 'selected' : ''}`}
                  onClick={() => setSelectedService(service)}
                  data-testid={`service-${service.id}`}
                >
                  <div className="service-info">
                    <span className="service-name">{service.name}</span>
                    <span className="service-desc">{service.description}</span>
                  </div>
                  <div className="service-price">{service.price}€</div>
                </div>
              ))}
            </div>
            
            <button 
              className="btn btn-primary full-width"
              disabled={!selectedService}
              onClick={() => setStep(2)}
              data-testid="next-step-1"
            >
              Continuer
            </button>
          </div>
        )}

        {/* Step 2: Choose Barber */}
        {step === 2 && (
          <div className="modal-step" data-testid="step-barber">
            <h3><User size={20} /> Choisissez votre coiffeur</h3>
            
            <div className="barbers-grid">
              {barbers.map(barber => (
                <div 
                  key={barber.id}
                  className={`barber-card ${selectedBarber?.id === barber.id ? 'selected' : ''}`}
                  onClick={() => setSelectedBarber(barber)}
                  data-testid={`barber-${barber.id}`}
                >
                  <div className="barber-avatar">
                    <User size={32} />
                  </div>
                  <span className="barber-name">{barber.name}</span>
                </div>
              ))}
            </div>
            
            <div className="modal-buttons">
              <button className="btn btn-secondary" onClick={() => setStep(1)}>Retour</button>
              <button 
                className="btn btn-primary"
                disabled={!selectedBarber}
                onClick={() => setStep(3)}
                data-testid="next-step-2"
              >
                Continuer
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Choose Date & Time */}
        {step === 3 && (
          <div className="modal-step" data-testid="step-datetime">
            <h3><Calendar size={20} /> Choisissez la date et l'heure</h3>
            
            <div className="datetime-picker">
              <div className="date-input">
                <label>Date</label>
                <input 
                  type="date" 
                  value={selectedDate}
                  onChange={e => {
                    setSelectedDate(e.target.value);
                    setSelectedTime('');
                  }}
                  min={getMinDate()}
                  max={getMaxDate()}
                  data-testid="date-input"
                />
              </div>
              
              {selectedDate && (
                <div className="time-slots">
                  <label>Heure {loadingSlots && <span className="loading-text">(chargement...)</span>}</label>
                  <div className="slots-grid">
                    {availableSlots.map(slot => (
                      <button
                        key={slot.time}
                        className={`time-slot ${selectedTime === slot.time ? 'selected' : ''} ${!slot.available ? 'unavailable' : ''}`}
                        onClick={() => slot.available && setSelectedTime(slot.time)}
                        disabled={!slot.available}
                        data-testid={`time-${slot.time}`}
                      >
                        {slot.time}
                        {!slot.available && <span className="slot-taken">Pris</span>}
                      </button>
                    ))}
                  </div>
                  {availableSlots.length === 0 && !loadingSlots && (
                    <p className="no-slots-message">Sélectionnez une date pour voir les créneaux disponibles</p>
                  )}
                </div>
              )}
            </div>
            
            <div className="modal-buttons">
              <button className="btn btn-secondary" onClick={() => setStep(2)}>Retour</button>
              <button 
                className="btn btn-primary"
                disabled={!selectedDate || !selectedTime}
                onClick={() => setStep(4)}
                data-testid="next-step-3"
              >
                Continuer
              </button>
            </div>
          </div>
        )}

        {/* Step 4: Customer Info */}
        {step === 4 && (
          <div className="modal-step" data-testid="step-info">
            <h3><Mail size={20} /> Vos informations</h3>
            
            <div className="booking-summary">
              <div className="summary-row">
                <span>Service:</span>
                <span>{selectedService?.name} - {selectedService?.price}€</span>
              </div>
              <div className="summary-row">
                <span>Coiffeur:</span>
                <span>{selectedBarber?.name}</span>
              </div>
              <div className="summary-row">
                <span>Date:</span>
                <span>{selectedDate} à {selectedTime}</span>
              </div>
            </div>

            <div className="form-group">
              <label>Nom complet</label>
              <input 
                type="text"
                value={customerName}
                onChange={e => setCustomerName(e.target.value)}
                placeholder="Votre nom"
                data-testid="input-name"
              />
            </div>
            
            <div className="form-group">
              <label>Email</label>
              <input 
                type="email"
                value={customerEmail}
                onChange={e => setCustomerEmail(e.target.value)}
                placeholder="votre@email.com"
                data-testid="input-email"
              />
            </div>
            
            <div className="form-group">
              <label>Téléphone</label>
              <input 
                type="tel"
                value={customerPhone}
                onChange={e => setCustomerPhone(e.target.value)}
                placeholder="06 XX XX XX XX"
                data-testid="input-phone"
              />
            </div>
            
            <div className="modal-buttons">
              <button className="btn btn-secondary" onClick={() => setStep(3)}>Retour</button>
              <button 
                className="btn btn-primary"
                disabled={!customerName || !customerEmail || !customerPhone || loading}
                onClick={handleSubmit}
                data-testid="confirm-booking"
              >
                {loading ? 'Confirmation...' : 'Confirmer le RDV'}
              </button>
            </div>
          </div>
        )}

        {/* Step 5: Confirmation */}
        {step === 5 && bookingResult && (
          <div className="modal-step confirmation" data-testid="step-confirmation">
            <div className="success-icon">
              <Check size={48} />
            </div>
            <h3>Réservation Confirmée !</h3>
            
            <div className="confirmation-number">
              {bookingResult.confirmation_number}
            </div>
            
            <p className="confirmation-text">
              Un email de confirmation a été envoyé à <strong>{customerEmail}</strong>
            </p>
            
            <div className="booking-summary final">
              <div className="summary-row">
                <span>Service:</span>
                <span>{selectedService?.name}</span>
              </div>
              <div className="summary-row">
                <span>Prix:</span>
                <span>{selectedService?.price}€</span>
              </div>
              <div className="summary-row">
                <span>Coiffeur:</span>
                <span>{selectedBarber?.name}</span>
              </div>
              <div className="summary-row">
                <span>Date:</span>
                <span>{selectedDate} à {selectedTime}</span>
              </div>
            </div>
            
            <div className="cancel-notice">
              <p>⚠️ Pour annuler ou modifier votre RDV, appelez-nous au <strong>{SALON_CONFIG.phone}</strong></p>
            </div>
            
            <button className="btn btn-primary full-width" onClick={resetForm}>
              Fermer
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// Composant Header
const Header = ({ activeSection, onBookingClick }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setMobileMenuOpen(false);
  };

  const navItems = [
    { id: 'accueil', label: 'ACCUEIL' },
    { id: 'services', label: 'SERVICES' },
    { id: 'galerie', label: 'GALERIE' },
    { id: 'avis', label: 'AVIS' },
    { id: 'contact', label: 'CONTACT' }
  ];

  return (
    <header className="header" data-testid="header">
      <div className="header-container">
        <div className="logo" data-testid="logo">
          <span className="logo-house">LE GRAND</span>
          <span className="logo-barber">PARIS</span>
        </div>

        {/* Desktop Navigation */}
        <nav className="nav-desktop" data-testid="nav-desktop">
          {navItems.map(item => (
            <button
              key={item.id}
              className={`nav-link ${activeSection === item.id ? 'active' : ''}`}
              onClick={() => scrollToSection(item.id)}
              data-testid={`nav-${item.id}`}
            >
              {item.label}
            </button>
          ))}
        </nav>

        <button 
          className="cta-button" 
          onClick={onBookingClick}
          data-testid="header-booking-btn"
        >
          <Calendar size={18} />
          <span>RÉSERVER</span>
        </button>

        {/* Mobile Menu Button */}
        <button 
          className="mobile-menu-btn"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          data-testid="mobile-menu-btn"
        >
          {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Navigation */}
      {mobileMenuOpen && (
        <nav className="nav-mobile" data-testid="nav-mobile">
          {navItems.map(item => (
            <button
              key={item.id}
              className={`nav-link ${activeSection === item.id ? 'active' : ''}`}
              onClick={() => scrollToSection(item.id)}
            >
              {item.label}
            </button>
          ))}
          <button className="btn btn-primary" onClick={() => { onBookingClick(); setMobileMenuOpen(false); }}>
            Réserver un RDV
          </button>
        </nav>
      )}
    </header>
  );
};

// Composant Hero
const Hero = ({ onBookingClick }) => {
  return (
    <section id="accueil" className="hero" data-testid="hero-section">
      <div className="hero-background">
        <img 
          src="https://images.unsplash.com/photo-1585747860715-2ba37e788b70?w=1920&h=1080&fit=crop" 
          alt="Salon de coiffure Le Grand Paris à Champigny-sur-Marne"
          className="hero-bg-image"
        />
        <div className="hero-overlay"></div>
      </div>
      
      <LightRays 
        raysOrigin="top-center"
        raysColor="#d4a853"
        raysSpeed={0.8}
        lightSpread={1.2}
        rayLength={2.5}
        pulsating={false}
        fadeDistance={1.2}
        saturation={0.8}
        followMouse={true}
        mouseInfluence={0.15}
        noiseAmount={0.05}
        distortion={0.1}
        className="hero-light-rays"
      />
      
      <div className="hero-content">
        <div className="hero-location">
          <span className="location-line"></span>
          <span className="location-text">{SALON_CONFIG.location}</span>
        </div>
        
        <h1 className="hero-title" data-testid="hero-title">
          <span className="title-house">LE GRAND</span>
          <span className="title-barber">PARIS</span>
        </h1>
        
        <p className="hero-tagline">{SALON_CONFIG.tagline}</p>
        
        <div className="hero-rating" data-testid="hero-rating">
          <div className="stars">
            {[...Array(5)].map((_, i) => (
              <Star 
                key={i} 
                size={20} 
                fill={i < Math.floor(SALON_CONFIG.rating) ? "#FFD700" : "transparent"}
                color="#FFD700"
              />
            ))}
          </div>
          <span className="rating-score">{SALON_CONFIG.rating.toFixed(1)}</span>
          <span className="rating-source">sur Google ({SALON_CONFIG.reviewCount} avis)</span>
        </div>
        
        <div className="hero-buttons">
          <button 
            className="btn btn-primary"
            onClick={onBookingClick}
            data-testid="hero-rdv-btn"
          >
            <Calendar size={20} />
            <span>Prendre RDV</span>
          </button>
          <a 
            href={`tel:${SALON_CONFIG.phone.replace(/\s/g, '')}`}
            className="btn btn-secondary"
            data-testid="hero-phone-btn"
          >
            <Phone size={20} />
            <span>{SALON_CONFIG.phone}</span>
          </a>
        </div>
      </div>
      
      <div className="scroll-indicator">
        <ChevronDown size={24} className="bounce" />
      </div>
    </section>
  );
};

// Composant Services
const Services = () => {
  return (
    <section id="services" className="services" data-testid="services-section">
      <div className="services-container">
        <h2 className="section-title">
          <span className="title-white">Nos</span>
          <span className="title-gold"> prestations</span>
        </h2>
        <p className="section-subtitle">
          Chaque prestation Le Grand Paris est réalisée avec précision et professionnalisme.
        </p>
        
        <div className="services-grid">
          {SERVICES.map((service, index) => (
            <div key={index} className="service-card" data-testid={`service-card-${index}`}>
              <div className="service-image">
                <img src={service.image} alt={service.title} />
              </div>
              <div className="service-content">
                <h3 className="service-title">{service.title}</h3>
                <p className="service-description">{service.description}</p>
              </div>
            </div>
          ))}
        </div>
        
        <div className="services-cta">
          <a 
            href={SALON_CONFIG.googleMapsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-primary"
            data-testid="find-salon-btn"
          >
            <MapPin size={20} />
            <span>Trouver le salon</span>
          </a>
        </div>
      </div>
    </section>
  );
};

// Composant Galerie
const Gallery = () => {
  return (
    <section id="galerie" className="gallery" data-testid="gallery-section">
      <div className="gallery-container">
        <h2 className="section-title left-align">
          <span className="title-white">GALERIE</span>
          <span className="title-gold"> PHOTOS</span>
        </h2>
        
        <div className="gallery-grid">
          <div className="gallery-main">
            <img src={GALLERY_PHOTOS[0]} alt="Coupe homme dégradé - Le Grand Paris Champigny" />
          </div>
          <div className="gallery-side">
            <img src={GALLERY_PHOTOS[1]} alt="Taille de barbe - Barber Champigny-sur-Marne" />
            <img src={GALLERY_PHOTOS[2]} alt="Coiffure femme - Salon Le Grand Paris" />
          </div>
        </div>
        
        <div className="gallery-row">
          {GALLERY_PHOTOS.slice(3).map((photo, index) => (
            <div key={index} className="gallery-item">
              <img src={photo} alt={`Coiffure et coupe de cheveux Le Grand Paris Champigny ${index + 4}`} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Composant Avis - Carrousel auto-défilant
const Reviews = () => {
  return (
    <section id="avis" className="reviews" data-testid="reviews-section">
      <div className="reviews-container">
        <div className="reviews-header">
          <div>
            <div className="section-label">
              <span className="label-line"></span>
              <span className="label-text">TÉMOIGNAGES</span>
            </div>
            <h2 className="section-title left-align">
              <span className="title-white">AVIS</span>
              <span className="title-gold"> CLIENTS</span>
            </h2>
          </div>
          <div className="reviews-rating-badge" data-testid="reviews-rating">
            <div className="rating-big">
              <span className="rating-number">{SALON_CONFIG.rating.toFixed(1)}</span>
              <div className="rating-stars">
                {[...Array(5)].map((_, i) => (
                  <Star 
                    key={i} 
                    size={18} 
                    fill="#FFD700"
                    color="#FFD700"
                  />
                ))}
              </div>
              <span className="rating-count">{SALON_CONFIG.reviewCount} avis Google</span>
            </div>
          </div>
        </div>
        
        {/* Carrousel auto-défilant */}
        <div className="reviews-marquee">
          <div className="reviews-track-auto">
            {/* Double les avis pour un défilement infini */}
            {[...REVIEWS, ...REVIEWS].map((review, index) => (
              <div key={`${review.id}-${index}`} className="review-card-marquee">
                <div className="review-card-header">
                  <div className="reviewer-avatar">
                    {review.name.charAt(0).toUpperCase()}
                  </div>
                  <div className="reviewer-info">
                    <span className="reviewer-name">{review.name}</span>
                    <div className="review-stars-small">
                      {[...Array(5)].map((_, i) => (
                        <Star 
                          key={i} 
                          size={12} 
                          fill={i < review.rating ? "#FFD700" : "transparent"}
                          color="#FFD700"
                        />
                      ))}
                    </div>
                  </div>
                </div>
                <p className="review-text-marquee">{review.text}</p>
                <div className="review-footer">
                  <span className="review-date">{review.date}</span>
                  <div className="review-source">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// Composant Contact
const Contact = ({ onBookingClick }) => {
  return (
    <section id="contact" className="contact" data-testid="contact-section">
      <div className="contact-container">
        <div className="contact-content">
          <div className="contact-info">
            <div className="section-label">
              <span className="label-line"></span>
              <span className="label-text">CONTACT</span>
            </div>
            <h2 className="section-title left-align">
              <span className="title-white">PRENEZ</span>
              <br />
              <span className="title-gold">RENDEZ-VOUS</span>
            </h2>
            <p className="contact-description">
              Réservez votre créneau en ligne ou appelez-nous directement. Notre équipe vous accueille 7j/7.
            </p>
            
            <div className="contact-buttons">
              <button 
                className="btn btn-primary"
                onClick={onBookingClick}
                data-testid="contact-booking"
              >
                <Calendar size={20} />
                <span>Réserver en ligne</span>
              </button>
              <a 
                href={`tel:${SALON_CONFIG.phone.replace(/\s/g, '')}`}
                className="btn btn-outline gold"
                data-testid="contact-phone"
              >
                <Phone size={20} />
                <span>Appeler</span>
              </a>
            </div>
            
            <div className="social-links">
              <span className="social-label">SUIVEZ-NOUS</span>
              <div className="social-icons">
                <a href={SALON_CONFIG.instagram} target="_blank" rel="noopener noreferrer" data-testid="social-instagram">
                  <Instagram size={20} />
                </a>
                <a href={SALON_CONFIG.tiktok} target="_blank" rel="noopener noreferrer" data-testid="social-tiktok">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                  </svg>
                </a>
              </div>
            </div>
            
            <div className="contact-map">
              <a 
                href={SALON_CONFIG.googleMapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="map-link"
              >
                Ouvrir dans Maps
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                  <polyline points="15 3 21 3 21 9" />
                  <line x1="10" y1="14" x2="21" y2="3" />
                </svg>
              </a>
              <div className="map-placeholder">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2627.0!2d2.518!3d48.82!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47e60d3f3f3f3f3f%3A0x0!2s149%20Av.%20Roger%20Salengro%2C%2094500%20Champigny-sur-Marne!5e0!3m2!1sfr!2sfr!4v1"
                  width="100%"
                  height="200"
                  style={{ border: 0, borderRadius: '12px' }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Google Maps"
                ></iframe>
              </div>
            </div>
          </div>
          
          <div className="contact-hours">
            <div className="hours-card">
              <div className="hours-header">
                <Clock size={24} />
                <h3>Horaires d'ouverture</h3>
              </div>
              <div className="hours-list">
                {SALON_CONFIG.hours.map((item, index) => (
                  <div key={index} className="hours-row">
                    <span className="day">{item.day}</span>
                    <span className="time">{item.time}</span>
                  </div>
                ))}
              </div>
              <div className="address-info">
                <MapPin size={20} />
                <div>
                  <p className="address">{SALON_CONFIG.address}</p>
                  <a 
                    href={SALON_CONFIG.googleMapsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="directions-link"
                  >
                    Obtenir l'itinéraire
                    <ChevronRight size={16} />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Composant Footer
const Footer = () => {
  return (
    <footer className="footer" data-testid="footer">
      <div className="footer-container">
        <div className="logo">
          <span className="logo-house">LE GRAND</span>
          <span className="logo-barber">PARIS</span>
        </div>
        <p className="copyright">
          © 2026 Le Grand Paris. Coiffure à Champigny-sur-Marne
        </p>
      </div>
    </footer>
  );
};

// Application principale
function App() {
  const [activeSection, setActiveSection] = useState('accueil');
  const [bookingModalOpen, setBookingModalOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const sections = ['accueil', 'services', 'galerie', 'avis', 'contact'];
      const scrollPosition = window.scrollY + 100;

      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetBottom = offsetTop + element.offsetHeight;
          
          if (scrollPosition >= offsetTop && scrollPosition < offsetBottom) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="App" data-testid="app">
      <Header activeSection={activeSection} onBookingClick={() => setBookingModalOpen(true)} />
      <main>
        <Hero onBookingClick={() => setBookingModalOpen(true)} />
        <Services />
        <Gallery />
        <Reviews />
        <Contact onBookingClick={() => setBookingModalOpen(true)} />
      </main>
      <Footer />
      <BookingModal isOpen={bookingModalOpen} onClose={() => setBookingModalOpen(false)} />
    </div>
  );
}

export default App;
